import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { FileText, ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSuccess, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { empDocumentService } from '@/services/empDocumentService'
import { employeesService } from '@/services/employeesService'
import type { Employee } from '@/api/types'

const schema = z.object({
  DocumentTypeId: z.number().min(0),
  DocumentName: z.string().min(1, 'Name is required'),
  DocumentDiscription: z.string().optional(),
  DocumentIssueDate: z.string().optional(),
  DoucumentExpiryDate: z.string().optional(),
  EmpId: z.number().min(1, 'Employee is required'),
})

type FormData = z.infer<typeof schema>

export const EmpDocumentsEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<Employee[]>([])
  const [documentTypes, setDocumentTypes] = useState<{ id: number; name: string }[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      DocumentTypeId: 0,
      DocumentName: '',
      DocumentDiscription: '',
      DocumentIssueDate: '',
      DoucumentExpiryDate: '',
      EmpId: 0,
    },
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        setEmployees(res.data ?? [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    const loadDocumentTypes = async () => {
      try {
        const list = await empDocumentService.getDocumentTypes()
        const options = (list || []).map((item: any) => {
          const id = item.id ?? item.documentTypeId ?? 0
          const name = item.name ?? item.nameEn ?? item.nameAr ?? item.documentTypeName ?? `Type ${id}`
          return { id: Number(id), name: String(name) }
        }).filter((o) => o.id > 0)
        setDocumentTypes(options)
      } catch {
        setDocumentTypes([])
      }
    }
    loadDocumentTypes()
  }, [])

  useEffect(() => {
    if (!isEditing || !id || !branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const doc = await empDocumentService.getById(Number(id))
        setValue('DocumentTypeId', doc.documentTypeId ?? 0)
        setValue('DocumentName', doc.documentName ?? '')
        setValue('DocumentDiscription', doc.documentDiscription ?? '')
        setValue('DocumentIssueDate', doc.documentIssueDate ? doc.documentIssueDate.slice(0, 10) : '')
        setValue('DoucumentExpiryDate', doc.doucumentExpiryDate ? doc.doucumentExpiryDate.slice(0, 10) : '')
        setValue('EmpId', doc.empId ?? 0)
      } catch (e: any) {
        toast.error(e?.message ?? 'Failed to load document')
        navigate('/emp-documents')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, branchId, setValue, navigate])

  const onSubmit = async (data: FormData) => {
    if (!branchId) {
      toast.error('Please select a branch.')
      return
    }
    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('Id', isEditing && id ? id : '0')
      formData.append('EmpId', String(data.EmpId))
      formData.append('DocumentTypeId', String(data.DocumentTypeId))
      formData.append('DocumentName', data.DocumentName)
      formData.append('DocumentDiscription', data.DocumentDiscription ?? '')
      formData.append('DocumentIssueDate', data.DocumentIssueDate ? `${data.DocumentIssueDate}T00:00:00.000Z` : '')
      formData.append('DoucumentExpiryDate', data.DoucumentExpiryDate ? `${data.DoucumentExpiryDate}T00:00:00.000Z` : '')
      formData.append('DocumentAttachUrl', '')
      formData.append('BranchId', String(branchId))
      if (selectedFile) formData.append('file', selectedFile)

      if (isEditing && id) {
        await empDocumentService.updateDocument(Number(id), formData)
        await showSuccess('Updated', 'Document updated successfully.')
      } else {
        await empDocumentService.addDocument(formData)
        await showSuccess('Saved', 'Document added successfully.')
      }
      navigate('/emp-documents')
    } catch (e: any) {
      await showError('Save failed', e?.message ?? e?.response?.data?.message ?? 'Failed to save.')
    } finally {
      setLoading(false)
    }
  }

  const employeeOptions = employees
    .filter((e) => e.id != null && e.empCode)
    .map((e) => {
      const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
      const lookupName = fullName ? `${e.empCode} — ${fullName}` : (e.empCode ?? `Employee ${e.id}`)
      return { value: String(e.id), label: lookupName }
    })

  const documentTypeOptions = [
    { value: '0', label: 'Select document type...' },
    ...documentTypes.map((dt) => ({ value: String(dt.id), label: dt.name })),
  ]

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Document" description="Add or edit employee document." />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">Please select a branch first.</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title="Document" description={isEditing ? 'Edit document' : 'Add new document'} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Document
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Employee *</Label>
                <SearchableSelect
                  options={[
                    { value: '0', label: 'Select employee' },
                    ...employeeOptions,
                  ]}
                  value={watch('EmpId')?.toString() ?? '0'}
                  onValueChange={(v) => setValue('EmpId', Number(v))}
                  placeholder="Select employee"
                  searchPlaceholder="Search employee..."
                  emptyMessage="No employee found"
                />
                {errors.EmpId && <p className="text-sm text-destructive">{errors.EmpId.message}</p>}
              </div>
            </div>

            {/* Row 1: Document Type, Name, Document Issue Date, Document Expiry Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Document Type *</Label>
                <SearchableSelect
                  options={documentTypeOptions}
                  value={watch('DocumentTypeId')?.toString() ?? '0'}
                  onValueChange={(v) => setValue('DocumentTypeId', Number(v))}
                  placeholder="Select document type..."
                  searchPlaceholder="Search..."
                  emptyMessage="No option"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="DocumentName">Name *</Label>
                <Input
                  id="DocumentName"
                  placeholder="DocumentName"
                  {...register('DocumentName')}
                  className={errors.DocumentName ? 'border-destructive' : ''}
                />
                {errors.DocumentName && (
                  <p className="text-sm text-destructive">{errors.DocumentName.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="DocumentIssueDate">Document Issue Date *</Label>
                <Input
                  id="DocumentIssueDate"
                  type="date"
                  {...register('DocumentIssueDate')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="DoucumentExpiryDate">Document Expiry Date *</Label>
                <Input
                  id="DoucumentExpiryDate"
                  type="date"
                  {...register('DoucumentExpiryDate')}
                />
              </div>
            </div>

            {/* Row 2: Description (full width) */}
            <div className="space-y-2">
              <Label htmlFor="DocumentDiscription">Discription *</Label>
              <textarea
                id="DocumentDiscription"
                placeholder="Please enter a Discription."
                rows={4}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                {...register('DocumentDiscription')}
              />
            </div>

            {/* Upload Document */}
            <div className="space-y-2">
              <Label>Upload Document</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              />
              <p className="text-xs text-muted-foreground">Choose file (optional)</p>
            </div>
          </CardContent>
        </Card>
        <div className="flex justify-end gap-2 mt-6">
          <Button type="button" variant="outline" onClick={() => navigate('/emp-documents')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return
          </Button>
          <Button type="submit" disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            Save
          </Button>
        </div>
      </form>
    </div>
  )
}
